/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../IEditor";
import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { createEventEmitter, emitEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { AuthProvidersParamsResolver } from "./Resolvers/AuthProvidersParamsResolver";
import { IAuthProvidersType } from "./AuthProvidersServiceTypes";
import { BP, importSchema, ISchemaConstObject, TSchemaConstObjectProps } from "@adapptio/lib-blueprint";

export interface IAuthProvidersServiceBaseResolvers {
	authProvidersParams: AuthProvidersParamsResolver;
}

export class AuthProvidersService {
	/** Logger facility */
	private logger: BrowserLogger;

	/* Dependency services */
	private apiService: IEditorApiService;

	/** Auth providers */
	private authProviders: IAuthProvidersType[] = [];

	/** Index of Auth providers name -> type object */
	private typeIndexByName: { [K: string]: IAuthProvidersType } = {};

	/** Emitted when auth providers have changed or reloaded */
	public onAuthProvidersChange = createEventEmitter<void>();

	/** Cached base resolver */
	private baseResolvers: IAuthProvidersServiceBaseResolvers;

	/**
	 * Service constructor
	 *
	 * @param dc Dependency Container
	 */
	constructor(dc: IEditor) {
		this.logger = dc.get("logger").facility("AuthProviders");

		this.apiService = dc.get("apiService");

		this.createBaseResolvers();
	}

	/**
	 * Initializes the service
	 */
	public async init(): Promise<void> {
		await this.loadAuthProviders();
	}

	/**
	 * Disposes the service
	 */
	public dispose(): void {
		removeAllEventListeners(this.onAuthProvidersChange);
	}

	/**
	 * Loads auth providers from the API
	 */
	private async loadAuthProviders(): Promise<void> {
		try {
			const apiRes = await this.apiService.getManifestDefinition();

			this.authProviders = [];
			this.typeIndexByName = {};

			apiRes.authProviders.forEach((item) => {
				try {
					// Create auth providers object
					const typeObject: IAuthProvidersType = {
						name: item.name,
						category: item.category,
						optionsSchema: BP.Const.Map({
							value: importSchema<ISchemaConstObject<TSchemaConstObjectProps>>(
								item.optionsSchema
							)
						})
					};

					// Add to indexes
					this.authProviders.push(typeObject);
					this.typeIndexByName[typeObject.name] = typeObject;
				} catch (err) {
					this.logger.error(`Failed to import auth providers "${item.integrationType}":`, err);
				}
			});

			this.logger.debug("Auth providers loaded:", this.authProviders);
			emitEvent(this.onAuthProvidersChange);
		} catch (err) {
			this.logger.error("Failed to load auth providers:", err);
		}
	}

	/**
	 * Creates and caches base resolvers
	 */
	private createBaseResolvers(): void {
		this.baseResolvers = {
			authProvidersParams: new AuthProvidersParamsResolver(this)
		};
	}

	/**
	 * Returns base resolvers
	 */
	public getBaseResolvers(): IAuthProvidersServiceBaseResolvers {
		return this.baseResolvers;
	}

	/**
	 * Returns all auth providers
	 */
	public getAuthProviders(): IAuthProvidersType[] {
		return this.authProviders;
	}

	/**
	 * Returns a single auth provider
	 *
	 * @param authProvidersType Auth provider name
	 */
	public getAuthProvidersType(authProvidersType: string): IAuthProvidersType {
		return this.typeIndexByName[authProvidersType] || null;
	}

	/**
	 * Returns whenever Auth provider exists
	 *
	 * @param authProvidersType Auth provider name
	 */
	public authProvidersTypeExists(authProvidersType: string): boolean {
		return !!this.typeIndexByName[authProvidersType];
	}
}
